import { format, subYears } from 'date-fns';
import { Calendar } from 'primereact/calendar';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { MultiSelect } from 'primereact/multiselect';
import React, { useState } from 'react';
import {
    BodyMediumRegular,
    BodySmallEmphasized,
    BodySmallRegular,
    H6Emphasized,
    Spinner,
    useToastController,
    XStack,
    YStack,
} from 'smoothie';

import { ConfirmModal } from '../../common/components/ConfirmModal';
import { Button } from '../../common/components/GroveButtons';
import {
    MutationErrorToastOptions,
    MutationSuccessToastOptions,
} from '../../common/components/Toasts';
import { formatDollars } from '../../common/utils/formatting';
import { IS_LOCAL } from '../../config/FruitfulEnvironment';
import {
    useGetBridgeftPerformance,
    useGetBridgeftReport,
    useResetBridgeftHouseholds,
    useSyncBridgeftPerformance,
} from '../hooks/useBridgeftData';
import type {
    AdminBridgeftAccountSummaryDto,
    AdminBridgeftDepositWithdrawalItemDto,
    AdminBridgeftIncomeItemDto,
    ApexAccountPerformanceSeriesItemDto,
    ApexPerformanceByHorizon,
    ApexPerformanceError,
} from '../types/BridgeftTypes';
import { ApexPerformanceHorizon } from '../types/BridgeftTypes';
import { BridgeftPerformanceChart } from './BridgeftPerformanceChart';

const SUB_REPORT_OPTIONS = [
    { label: 'Deposits & Withdrawals', value: 'deposits_withdrawals' },
    { label: 'Buy/Sells', value: 'buy_sells' },
    { label: 'Income', value: 'income' },
    { label: 'Account Summary', value: 'account_summary' },
    { label: 'Performance Summary', value: 'performance_summary' },
    { label: 'Performance Chart', value: 'performance_chart' },
    { label: 'Net Investment Chart', value: 'net_investment_chart' },
    { label: 'Appraisals', value: 'appraisals' },
    { label: 'Realized Gain/Loss', value: 'realized_gain_loss' },
    { label: 'Security Performance', value: 'security_performance' },
    { label: 'Management Fees', value: 'management_fees' },
    {
        label: 'Allocation & Performance',
        value: 'allocation_and_performance_summary',
    },
    {
        label: 'Asset Allocation/Top Holdings',
        value: 'asset_allocation_top_holdings',
    },
    { label: 'Portfolio Snapshot', value: 'portfolio_snapshot' },
    { label: 'Consolidated Summary', value: 'consolidated_summary' },
    { label: 'Benchmark Performance', value: 'benchmark_perf_summary' },
    { label: 'Risk/Return Chart', value: 'risk_return_chart' },
    {
        label: 'Target vs Actual Allocation',
        value: 'target_vs_actual_allocation',
    },
];

const HORIZON_LABELS: Record<ApexPerformanceHorizon, string> = {
    [ApexPerformanceHorizon['1M']]: '1 Month',
    [ApexPerformanceHorizon['3M']]: '3 Months',
    [ApexPerformanceHorizon['6M']]: '6 Months',
    [ApexPerformanceHorizon['1Y']]: '1 Year',
    [ApexPerformanceHorizon.ALL]: 'All Time',
};

const PerformanceSummaryTable = ({
    performance,
}: {
    performance: ApexPerformanceByHorizon;
}) => {
    const rows: Array<ApexAccountPerformanceSeriesItemDto & { label: string }> =
        Object.values(ApexPerformanceHorizon).map((horizon) => ({
            ...performance[horizon],
            label: HORIZON_LABELS[horizon],
        }));

    return (
        <DataTable
            value={rows}
            size="small"
            tableStyle={{ width: '100%', fontSize: '0.875rem' }}
        >
            <Column
                field="label"
                header="Horizon"
                body={(row: (typeof rows)[number]) => (
                    <BodySmallEmphasized>{row.label}</BodySmallEmphasized>
                )}
            />
            <Column
                header="Start Value"
                body={(row: (typeof rows)[number]) => {
                    const first = row.datapoints?.[0];
                    return (
                        <BodySmallRegular>
                            {first != null
                                ? formatDollars(first.accountValuation)
                                : '-'}
                        </BodySmallRegular>
                    );
                }}
            />
            <Column
                header="End Value"
                body={(row: (typeof rows)[number]) => {
                    const last = row.datapoints?.[row.datapoints.length - 1];
                    return (
                        <BodySmallRegular>
                            {last != null
                                ? formatDollars(last.accountValuation)
                                : '-'}
                        </BodySmallRegular>
                    );
                }}
            />
            <Column
                field="amountReturn"
                header="$ Return"
                body={(row: (typeof rows)[number]) => (
                    <BodySmallRegular>
                        {formatDollars(row.amountReturn)}
                    </BodySmallRegular>
                )}
            />
            <Column
                field="percentageReturn"
                header="% Return"
                body={(row: (typeof rows)[number]) => (
                    <BodySmallRegular>
                        {row.percentageReturn != null
                            ? `${row.percentageReturn.toFixed(2)}%`
                            : '-'}
                    </BodySmallRegular>
                )}
            />
            <Column
                field="datapoints"
                header="Datapoints"
                body={(row: (typeof rows)[number]) => (
                    <BodySmallRegular>
                        {row.datapoints?.length ?? 0}
                    </BodySmallRegular>
                )}
            />
        </DataTable>
    );
};

type LookupScope =
    | { type: 'account'; accountNumber: string }
    | { type: 'household'; householdId: string };

const PerformanceErrorsTable = ({
    errors,
}: {
    errors: ApexPerformanceError[];
}) => {
    if (errors.length === 0) return null;

    interface ErrorRow {
        type: string;
        message: string;
        detail: string;
    }

    const rows: ErrorRow[] = errors.flatMap((err): ErrorRow[] => {
        if (err.type === 'DATE_GAP') {
            return err.gaps.map((gap) => {
                const missingLabel = [
                    gap.missingDays != null ? `${gap.missingDays} days` : null,
                    gap.missingWeeks != null
                        ? `${gap.missingWeeks} weeks`
                        : null,
                ]
                    .filter(Boolean)
                    .join(', ');
                return {
                    type: err.type,
                    message: err.message,
                    detail: `${gap.from} → ${gap.to} [${gap.series}]${missingLabel.length > 0 ? ` (${missingLabel} missing)` : ''}`,
                };
            });
        }
        return err.occurrences.map((occ) => ({
            type: err.type,
            message: err.message,
            detail: `Step: ${occ.step}, prev value: ${formatDollars(occ.previousValue)}, net contribution: ${formatDollars(occ.netContribution)}`,
        }));
    });

    return (
        <YStack gap={4}>
            <BodySmallEmphasized color="$orange1100">
                Data Quality Errors ({errors.length})
            </BodySmallEmphasized>
            <DataTable
                value={rows}
                size="small"
                tableStyle={{ width: '100%', fontSize: '0.75rem' }}
                paginator
                rows={10}
            >
                <Column
                    field="type"
                    header="Type"
                    body={(row: ErrorRow) => (
                        <BodySmallEmphasized color="$red1100">
                            {row.type}
                        </BodySmallEmphasized>
                    )}
                />
                <Column
                    field="message"
                    header="Message"
                    body={(row: ErrorRow) => (
                        <BodySmallRegular>{row.message}</BodySmallRegular>
                    )}
                />
                <Column
                    field="detail"
                    header="Detail"
                    body={(row: ErrorRow) => (
                        <BodySmallRegular>{row.detail}</BodySmallRegular>
                    )}
                />
            </DataTable>
        </YStack>
    );
};

const PerformanceSection = ({ scope }: { scope: LookupScope }) => {
    const queryParams =
        scope.type === 'household'
            ? { householdId: scope.householdId }
            : { accountNumber: scope.accountNumber };
    const isEnabled =
        scope.type === 'household'
            ? scope.householdId.length > 0
            : scope.accountNumber.length > 0;

    const { data, isLoading, error, refetch } = useGetBridgeftPerformance(
        queryParams,
        { enabled: isEnabled },
    );
    const [selectedHorizon, setSelectedHorizon] =
        useState<ApexPerformanceHorizon>(ApexPerformanceHorizon.ALL);

    const performanceErrors = data?.errors ?? [];
    const selectedSeries = data?.[selectedHorizon] ?? null;

    return (
        <YStack gap={12}>
            <XStack justifyContent="space-between" alignItems="center">
                <H6Emphasized>Performance (Member View)</H6Emphasized>
                <Button
                    variant="secondary"
                    onPress={() => {
                        void refetch();
                    }}
                >
                    Refresh
                </Button>
            </XStack>
            <BodySmallRegular>
                Same normalized performance data the member sees (5 horizons via
                BridgeFT).
            </BodySmallRegular>
            {isLoading && <Spinner />}
            {error != null && (
                <BodySmallEmphasized color="$red1100">
                    Error:{' '}
                    {error instanceof Error ? error.message : String(error)}
                </BodySmallEmphasized>
            )}
            {data != null && (
                <YStack gap={16}>
                    <PerformanceErrorsTable errors={performanceErrors} />
                    <XStack gap={8} flexWrap="wrap">
                        {Object.values(ApexPerformanceHorizon).map(
                            (horizon) => (
                                <Button
                                    key={horizon}
                                    variant={
                                        selectedHorizon === horizon
                                            ? 'primary'
                                            : 'secondary'
                                    }
                                    size="small"
                                    onPress={() => {
                                        setSelectedHorizon(horizon);
                                    }}
                                >
                                    {horizon === ApexPerformanceHorizon.ALL
                                        ? 'All'
                                        : horizon}
                                </Button>
                            ),
                        )}
                    </XStack>
                    {selectedSeries != null &&
                        selectedSeries.datapoints.length > 0 && (
                            <YStack
                                backgroundColor="$white"
                                borderRadius={16}
                                padding={16}
                            >
                                <BridgeftPerformanceChart
                                    datapoints={selectedSeries.datapoints}
                                />
                            </YStack>
                        )}
                    <PerformanceSummaryTable performance={data} />
                </YStack>
            )}
        </YStack>
    );
};

const DepositWithdrawalTable = ({
    items,
    total,
}: {
    items: AdminBridgeftDepositWithdrawalItemDto[];
    total: number | null;
}) => (
    <YStack gap={4}>
        <XStack justifyContent="space-between" alignItems="center">
            <BodySmallEmphasized>
                Deposits & Withdrawals ({items.length} items)
            </BodySmallEmphasized>
            {total != null && (
                <BodySmallRegular>
                    Total: {formatDollars(total)}
                </BodySmallRegular>
            )}
        </XStack>
        <DataTable
            value={items}
            size="small"
            tableStyle={{ width: '100%', fontSize: '0.75rem' }}
            emptyMessage="No deposit/withdrawal data"
            paginator
            rows={10}
        >
            <Column
                field="transactionDate"
                header="Date"
                body={(row: AdminBridgeftDepositWithdrawalItemDto) => (
                    <BodySmallRegular>
                        {row.transactionDate ?? '-'}
                    </BodySmallRegular>
                )}
            />
            <Column
                field="type"
                header="Type"
                body={(row: AdminBridgeftDepositWithdrawalItemDto) => (
                    <BodySmallRegular>{row.type ?? '-'}</BodySmallRegular>
                )}
            />
            <Column
                field="description"
                header="Description"
                body={(row: AdminBridgeftDepositWithdrawalItemDto) => (
                    <BodySmallRegular>
                        {row.description ?? '-'}
                    </BodySmallRegular>
                )}
            />
            <Column
                field="securityDescription"
                header="Security"
                body={(row: AdminBridgeftDepositWithdrawalItemDto) => (
                    <BodySmallRegular>
                        {row.securityDescription ?? '-'}
                    </BodySmallRegular>
                )}
            />
            <Column
                field="amount"
                header="Amount"
                body={(row: AdminBridgeftDepositWithdrawalItemDto) => (
                    <BodySmallRegular>
                        {row.amount != null ? formatDollars(row.amount) : '-'}
                    </BodySmallRegular>
                )}
            />
            <Column
                field="isCancel"
                header="Canceled"
                body={(row: AdminBridgeftDepositWithdrawalItemDto) => (
                    <BodySmallRegular>
                        {row.isCancel === true
                            ? 'Yes'
                            : row.isCancel === false
                              ? 'No'
                              : '-'}
                    </BodySmallRegular>
                )}
            />
        </DataTable>
    </YStack>
);

const IncomeTable = ({
    items,
    total,
}: {
    items: AdminBridgeftIncomeItemDto[];
    total: number | null;
}) => (
    <YStack gap={4}>
        <XStack justifyContent="space-between" alignItems="center">
            <BodySmallEmphasized>
                Income ({items.length} items)
            </BodySmallEmphasized>
            {total != null && (
                <BodySmallRegular>
                    Total: {formatDollars(total)}
                </BodySmallRegular>
            )}
        </XStack>
        <DataTable
            value={items}
            size="small"
            tableStyle={{ width: '100%', fontSize: '0.75rem' }}
            emptyMessage="No income data"
            paginator
            rows={10}
        >
            <Column
                field="transactionDate"
                header="Date"
                body={(row: AdminBridgeftIncomeItemDto) => (
                    <BodySmallRegular>
                        {row.transactionDate ?? '-'}
                    </BodySmallRegular>
                )}
            />
            <Column
                field="type"
                header="Type"
                body={(row: AdminBridgeftIncomeItemDto) => (
                    <BodySmallRegular>{row.type ?? '-'}</BodySmallRegular>
                )}
            />
            <Column
                field="description"
                header="Description"
                body={(row: AdminBridgeftIncomeItemDto) => (
                    <BodySmallRegular>
                        {row.description ?? '-'}
                    </BodySmallRegular>
                )}
            />
            <Column
                field="securityDescription"
                header="Security"
                body={(row: AdminBridgeftIncomeItemDto) => (
                    <BodySmallRegular>
                        {row.securityDescription ?? '-'}
                    </BodySmallRegular>
                )}
            />
            <Column
                field="amount"
                header="Amount"
                body={(row: AdminBridgeftIncomeItemDto) => (
                    <BodySmallRegular>
                        {row.amount != null ? formatDollars(row.amount) : '-'}
                    </BodySmallRegular>
                )}
            />
            <Column
                field="isPerformanceImpact"
                header="Perf Impact"
                body={(row: AdminBridgeftIncomeItemDto) => (
                    <BodySmallRegular>
                        {row.isPerformanceImpact === true
                            ? 'Yes'
                            : row.isPerformanceImpact === false
                              ? 'No'
                              : '-'}
                    </BodySmallRegular>
                )}
            />
        </DataTable>
    </YStack>
);

const AccountSummaryDisplay = ({
    data,
}: {
    data: AdminBridgeftAccountSummaryDto;
}) => {
    const balancePoints = data.daily_itd_chart?.balance?.points ?? [];
    const contributionPoints =
        data.daily_itd_chart?.cumulative_contribution?.points ?? [];

    return (
        <YStack gap={12}>
            <BodySmallEmphasized>Account Summary</BodySmallEmphasized>

            {data.inception != null && (
                <XStack gap={24} flexWrap="wrap">
                    <YStack>
                        <BodySmallRegular>Beginning Balance</BodySmallRegular>
                        <BodySmallEmphasized>
                            {data.inception.beginning_balance != null
                                ? formatDollars(
                                      data.inception.beginning_balance,
                                  )
                                : '-'}
                        </BodySmallEmphasized>
                    </YStack>
                    <YStack>
                        <BodySmallRegular>Ending Balance</BodySmallRegular>
                        <BodySmallEmphasized>
                            {data.inception.ending_balance != null
                                ? formatDollars(data.inception.ending_balance)
                                : '-'}
                        </BodySmallEmphasized>
                    </YStack>
                    <YStack>
                        <BodySmallRegular>Net Appreciation</BodySmallRegular>
                        <BodySmallEmphasized>
                            {data.inception.net_appreciation != null
                                ? formatDollars(data.inception.net_appreciation)
                                : '-'}
                        </BodySmallEmphasized>
                    </YStack>
                    <YStack>
                        <BodySmallRegular>Net Contributions</BodySmallRegular>
                        <BodySmallEmphasized>
                            {data.inception.net_contributions != null
                                ? formatDollars(
                                      data.inception.net_contributions,
                                  )
                                : '-'}
                        </BodySmallEmphasized>
                    </YStack>
                </XStack>
            )}

            {data.latest_performance != null && (
                <XStack gap={24} flexWrap="wrap">
                    <YStack>
                        <BodySmallRegular>PTD Return</BodySmallRegular>
                        <BodySmallEmphasized>
                            {data.latest_performance.ptd_return != null
                                ? `${data.latest_performance.ptd_return.toFixed(2)}%`
                                : '-'}
                        </BodySmallEmphasized>
                    </YStack>
                    <YStack>
                        <BodySmallRegular>YTD Return</BodySmallRegular>
                        <BodySmallEmphasized>
                            {data.latest_performance.ytd_return != null
                                ? `${data.latest_performance.ytd_return.toFixed(2)}%`
                                : '-'}
                        </BodySmallEmphasized>
                    </YStack>
                    <YStack>
                        <BodySmallRegular>ITD Return</BodySmallRegular>
                        <BodySmallEmphasized>
                            {data.latest_performance.itd_return != null
                                ? `${data.latest_performance.itd_return.toFixed(2)}%`
                                : '-'}
                        </BodySmallEmphasized>
                    </YStack>
                </XStack>
            )}

            {balancePoints.length > 0 && (
                <YStack gap={8}>
                    <BodySmallEmphasized>
                        Net Investment Chart ({balancePoints.length} balance
                        points, {contributionPoints.length} contribution points)
                    </BodySmallEmphasized>
                    <DataTable
                        value={balancePoints}
                        size="small"
                        tableStyle={{ width: '100%', fontSize: '0.75rem' }}
                        emptyMessage="No balance data"
                        paginator
                        rows={10}
                    >
                        <Column
                            field="time"
                            header="Date"
                            body={(row: { time: string }) => (
                                <BodySmallRegular>{row.time}</BodySmallRegular>
                            )}
                        />
                        <Column
                            field="value"
                            header="Balance"
                            body={(row: { value: number }) => (
                                <BodySmallRegular>
                                    {formatDollars(row.value)}
                                </BodySmallRegular>
                            )}
                        />
                    </DataTable>
                </YStack>
            )}

            {contributionPoints.length > 0 && (
                <YStack gap={8}>
                    <BodySmallEmphasized>
                        Cumulative Contributions
                    </BodySmallEmphasized>
                    <DataTable
                        value={contributionPoints}
                        size="small"
                        tableStyle={{ width: '100%', fontSize: '0.75rem' }}
                        emptyMessage="No contribution data"
                        paginator
                        rows={10}
                    >
                        <Column
                            field="time"
                            header="Date"
                            body={(row: { time: string }) => (
                                <BodySmallRegular>{row.time}</BodySmallRegular>
                            )}
                        />
                        <Column
                            field="value"
                            header="Cumulative Contribution"
                            body={(row: { value: number }) => (
                                <BodySmallRegular>
                                    {formatDollars(row.value)}
                                </BodySmallRegular>
                            )}
                        />
                    </DataTable>
                </YStack>
            )}

            <pre
                style={{
                    fontSize: '0.65rem',
                    maxHeight: 200,
                    overflow: 'auto',
                    backgroundColor: '#f5f5f5',
                    padding: 12,
                    borderRadius: 8,
                }}
            >
                {JSON.stringify(data, null, 2)}
            </pre>
        </YStack>
    );
};

const RawSubReportDisplay = ({
    reportKey,
    data,
}: {
    reportKey: string;
    data: unknown;
}) => (
    <YStack gap={4}>
        <BodySmallEmphasized>{reportKey}</BodySmallEmphasized>
        <pre
            style={{
                fontSize: '0.7rem',
                maxHeight: 300,
                overflow: 'auto',
                backgroundColor: '#f5f5f5',
                padding: 12,
                borderRadius: 8,
            }}
        >
            {JSON.stringify(data, null, 2)}
        </pre>
    </YStack>
);

const SubReportSection = ({ scope }: { scope: LookupScope }) => {
    const today = new Date();
    const [fromDate, setFromDate] = useState<Date>(subYears(today, 1));
    const [toDate, setToDate] = useState<Date>(today);
    const [selectedSubReports, setSelectedSubReports] = useState<string[]>([
        'net_investment_chart',
        'account_summary',
        'deposits_withdrawals',
        'buy_sells',
        'income',
    ]);
    const [queryParams, setQueryParams] = useState<{
        accountNumber?: string;
        householdId?: string;
        subReports: string;
        startDate: string;
        endDate: string;
        searchKey: number;
    } | null>(null);

    const { data, isLoading, error } = useGetBridgeftReport(
        queryParams ?? {
            subReports: '',
            startDate: '',
            endDate: '',
            searchKey: 0,
        },
        { enabled: queryParams != null },
    );

    const handleSearch = () => {
        const identifier =
            scope.type === 'household'
                ? scope.householdId
                : scope.accountNumber;
        if (identifier.length === 0 || selectedSubReports.length === 0) return;
        setQueryParams((prev) => ({
            ...(scope.type === 'household'
                ? { householdId: scope.householdId }
                : { accountNumber: scope.accountNumber }),
            subReports: selectedSubReports.join(','),
            startDate: format(fromDate, 'yyyy-MM-dd'),
            endDate: format(toDate, 'yyyy-MM-dd'),
            searchKey: (prev?.searchKey ?? 0) + 1,
        }));
    };

    return (
        <YStack gap={12}>
            <H6Emphasized>Sub-Report Explorer</H6Emphasized>
            <BodySmallRegular>
                Query any BridgeFT sub-report for this account with date range
                controls.
            </BodySmallRegular>

            <XStack gap={16} alignItems="center" flexWrap="wrap">
                <YStack gap={4}>
                    <BodySmallEmphasized>Sub-Reports</BodySmallEmphasized>
                    <MultiSelect
                        value={selectedSubReports}
                        options={SUB_REPORT_OPTIONS}
                        onChange={(e) => {
                            setSelectedSubReports(e.value as string[]);
                        }}
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select sub-reports"
                        display="chip"
                        style={{ minWidth: 300, maxWidth: 500 }}
                    />
                </YStack>
                <XStack alignItems="center" gap={8}>
                    <BodyMediumRegular>From:</BodyMediumRegular>
                    <Calendar
                        value={fromDate}
                        onChange={(e) => {
                            if (e.value instanceof Date) setFromDate(e.value);
                        }}
                        showIcon
                        dateFormat="mm/dd/yy"
                        aria-label="From date"
                        maxDate={toDate}
                    />
                </XStack>
                <XStack alignItems="center" gap={8}>
                    <BodyMediumRegular>To:</BodyMediumRegular>
                    <Calendar
                        value={toDate}
                        onChange={(e) => {
                            if (e.value instanceof Date) setToDate(e.value);
                        }}
                        showIcon
                        dateFormat="mm/dd/yy"
                        aria-label="To date"
                        minDate={fromDate}
                        maxDate={today}
                    />
                </XStack>
                <Button
                    variant="primary"
                    onPress={handleSearch}
                    disabled={selectedSubReports.length === 0}
                >
                    Fetch Reports
                </Button>
            </XStack>

            {queryParams != null && (
                <YStack gap={16}>
                    {isLoading && <Spinner />}
                    {error != null && (
                        <BodySmallEmphasized color="$red1100">
                            Error:{' '}
                            {error instanceof Error
                                ? error.message
                                : String(error)}
                        </BodySmallEmphasized>
                    )}
                    {data != null && (
                        <YStack gap={16}>
                            <XStack gap={16} flexWrap="wrap">
                                <BodySmallRegular>
                                    Account: {data.account.accountNumber}
                                    {data.account.apexAccountId != null
                                        ? ` (Apex: ${data.account.apexAccountId})`
                                        : ''}{' '}
                                    — BridgeFT: {data.account.bridgeftAccountId}
                                </BodySmallRegular>
                                <BodySmallRegular>
                                    Date range: {data.dateRange.start} to{' '}
                                    {data.dateRange.end}
                                </BodySmallRegular>
                            </XStack>

                            {data.subReports.account_summary != null && (
                                <AccountSummaryDisplay
                                    data={data.subReports.account_summary}
                                />
                            )}

                            {data.subReports.deposit_withdrawal != null && (
                                <DepositWithdrawalTable
                                    items={
                                        data.subReports.deposit_withdrawal.items
                                    }
                                    total={
                                        data.subReports.deposit_withdrawal.total
                                    }
                                />
                            )}

                            {data.subReports.income_expense != null && (
                                <IncomeTable
                                    items={data.subReports.income_expense.items}
                                    total={data.subReports.income_expense.total}
                                />
                            )}

                            {data.subReports.buysell != null && (
                                <YStack gap={4}>
                                    <BodySmallEmphasized>
                                        Buy/Sells (
                                        {data.subReports.buysell.items.length}{' '}
                                        items)
                                    </BodySmallEmphasized>
                                    <DataTable
                                        value={data.subReports.buysell.items}
                                        size="small"
                                        tableStyle={{
                                            width: '100%',
                                            fontSize: '0.75rem',
                                        }}
                                        emptyMessage="No buy/sell data"
                                        paginator
                                        rows={10}
                                    >
                                        <Column
                                            field="securityDescription"
                                            header="Security"
                                            body={(row: {
                                                securityDescription:
                                                    | string
                                                    | null;
                                            }) => (
                                                <BodySmallRegular>
                                                    {row.securityDescription ??
                                                        '-'}
                                                </BodySmallRegular>
                                            )}
                                        />
                                        <Column
                                            field="lucaData"
                                            header="Details (Raw)"
                                            body={(row: {
                                                lucaData: object | null;
                                            }) => (
                                                <pre
                                                    style={{
                                                        fontSize: '0.65rem',
                                                        margin: 0,
                                                    }}
                                                >
                                                    {row.lucaData != null
                                                        ? JSON.stringify(
                                                              row.lucaData,
                                                              null,
                                                              1,
                                                          )
                                                        : '-'}
                                                </pre>
                                            )}
                                        />
                                    </DataTable>
                                </YStack>
                            )}

                            {Object.entries(data.subReports)
                                .filter(
                                    ([key]) =>
                                        key !== 'account_summary' &&
                                        key !== 'deposit_withdrawal' &&
                                        key !== 'buysell' &&
                                        key !== 'income_expense',
                                )
                                .map(([key, value]) => (
                                    <RawSubReportDisplay
                                        key={key}
                                        reportKey={key}
                                        data={value}
                                    />
                                ))}
                        </YStack>
                    )}
                </YStack>
            )}
        </YStack>
    );
};

const SyncPerformanceSection = () => {
    const toast = useToastController();
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [startedWorkflowId, setStartedWorkflowId] = useState<string | null>(
        null,
    );

    const { mutateAsync: syncPerformance, isPending } =
        useSyncBridgeftPerformance();

    const handleConfirm = async () => {
        setIsConfirmModalOpen(false);
        try {
            const result = await syncPerformance({});
            setStartedWorkflowId(result.workflowID);
            toast.show(
                `BridgeFT job started (${result.workflowID}). Watch #im-ops or the DBOS Dashboard tab.`,
                MutationSuccessToastOptions,
            );
        } catch (error) {
            toast.show(
                'Could not start BridgeFT job',
                MutationErrorToastOptions(error),
            );
        }
    };

    return (
        <YStack
            gap={12}
            backgroundColor="$gray100"
            padding={16}
            borderRadius={8}
            role="region"
            aria-label="BridgeFT Performance Sync Control"
        >
            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => {
                    setIsConfirmModalOpen(false);
                }}
                onConfirm={() => {
                    void handleConfirm();
                }}
                description="Start the nightly BridgeFT identity sync and performance cache rebuild? The job runs in the background."
                yesText="Start job"
            />
            <H6Emphasized>Refresh cached performance</H6Emphasized>
            <BodySmallRegular>
                Queues the same nightly job on demand: first the BridgeFT
                identity sync (households and account IDs), then the performance
                cache rebuild for every household and account. The request
                returns as soon as the workflow is queued. Results are posted to
                #im-ops; progress is on the DBOS Dashboard tab.
            </BodySmallRegular>
            <XStack gap={16} alignItems="center">
                <Button
                    variant="primary"
                    size="small"
                    disabled={isPending}
                    onPress={() => {
                        setIsConfirmModalOpen(true);
                    }}
                    aria-busy={isPending}
                    aria-disabled={isPending}
                >
                    {isPending
                        ? 'Starting BridgeFT job...'
                        : 'Run identity sync + performance cache'}
                </Button>
            </XStack>
            {startedWorkflowId != null && (
                <BodySmallRegular>
                    Last started workflow: {startedWorkflowId}
                </BodySmallRegular>
            )}
        </YStack>
    );
};

const ResetLocalHouseholdsSection = () => {
    const toast = useToastController();
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

    const { mutateAsync: resetHouseholds, isPending } =
        useResetBridgeftHouseholds();

    const handleConfirm = async () => {
        setIsConfirmModalOpen(false);
        try {
            const result = await resetHouseholds({});
            toast.show(
                `Reset ${result.membershipsCleared} of ${result.membershipsFound} memberships (${result.householdsDeleted} households deleted, ${result.membershipsFailed} failed).`,
                MutationSuccessToastOptions,
            );
        } catch (error) {
            toast.show(
                'Could not reset local BridgeFT households',
                MutationErrorToastOptions(error),
            );
        }
    };

    return (
        <YStack
            gap={12}
            backgroundColor="$gray100"
            padding={16}
            borderRadius={8}
            role="region"
            aria-label="Local BridgeFT Household Reset"
        >
            <ConfirmModal
                isOpen={isConfirmModalOpen}
                onClose={() => {
                    setIsConfirmModalOpen(false);
                }}
                onConfirm={() => {
                    void handleConfirm();
                }}
                description="Delete every BridgeFT household linked to a membership in this local database, then clear household IDs, account IDs, and performance caches? This cannot be undone."
                yesText="Delete households"
            />
            <H6Emphasized>Reset local BridgeFT households</H6Emphasized>
            <BodySmallRegular>
                Local development only. Deletes BridgeFT households named for
                memberships in this database and clears stored household IDs,
                account IDs, and performance caches so identity sync can start
                clean.
            </BodySmallRegular>
            <XStack gap={16} alignItems="center">
                <Button
                    variant="secondary"
                    size="small"
                    tone="danger"
                    disabled={isPending}
                    onPress={() => {
                        setIsConfirmModalOpen(true);
                    }}
                    aria-busy={isPending}
                    aria-disabled={isPending}
                >
                    {isPending
                        ? 'Resetting households...'
                        : 'Reset local households'}
                </Button>
            </XStack>
        </YStack>
    );
};

type LookupMode = 'account' | 'household';

export const ImOpsBridgeftPerformancePanel = () => {
    const [lookupMode, setLookupMode] = useState<LookupMode>('account');
    const [inputValue, setInputValue] = useState('');
    const [activeScope, setActiveScope] = useState<LookupScope | null>(null);

    const handleLookup = () => {
        const trimmed = inputValue.trim();
        if (trimmed.length === 0) return;
        if (lookupMode === 'household') {
            setActiveScope({ type: 'household', householdId: trimmed });
        } else {
            setActiveScope({ type: 'account', accountNumber: trimmed });
        }
    };

    const handleModeChange = (mode: LookupMode) => {
        setLookupMode(mode);
        setActiveScope(null);
        setInputValue('');
    };

    return (
        <YStack gap={32}>
            <XStack
                justifyContent="space-between"
                width="100%"
                alignItems="center"
                role="banner"
            >
                <H6Emphasized>BridgeFT Performance & Reports</H6Emphasized>
            </XStack>

            <SyncPerformanceSection />
            {IS_LOCAL && <ResetLocalHouseholdsSection />}

            <YStack gap={12}>
                <BodySmallRegular>
                    Look up BridgeFT performance data and sub-reports by account
                    number or household ID.
                </BodySmallRegular>
                <XStack gap={12} alignItems="center" flexWrap="wrap">
                    <XStack gap={4}>
                        <Button
                            variant={
                                lookupMode === 'account'
                                    ? 'primary'
                                    : 'secondary'
                            }
                            size="small"
                            onPress={() => {
                                handleModeChange('account');
                            }}
                        >
                            Account Number
                        </Button>
                        <Button
                            variant={
                                lookupMode === 'household'
                                    ? 'primary'
                                    : 'secondary'
                            }
                            size="small"
                            onPress={() => {
                                handleModeChange('household');
                            }}
                        >
                            Household ID
                        </Button>
                    </XStack>
                    <input
                        type="text"
                        value={inputValue}
                        onChange={(e) => {
                            setInputValue(e.target.value);
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleLookup();
                        }}
                        placeholder={
                            lookupMode === 'household'
                                ? 'BridgeFT Household ID'
                                : 'Account Number'
                        }
                        style={{
                            padding: '8px 12px',
                            borderRadius: 8,
                            border: '1px solid #ccc',
                            fontSize: '0.875rem',
                            width: 280,
                        }}
                    />
                    <Button
                        variant="primary"
                        onPress={handleLookup}
                        disabled={inputValue.trim().length === 0}
                    >
                        Look Up
                    </Button>
                </XStack>
            </YStack>

            {activeScope != null && (
                <YStack gap={32}>
                    <PerformanceSection scope={activeScope} />
                    <SubReportSection scope={activeScope} />
                </YStack>
            )}
        </YStack>
    );
};
